/**
 * @file CatalogCardGroups.tsx
 * @description Группы вкладки «Каталог → Карточка товара» (phase19.16 Р2а;
 * uiux-design-r2a §2; P3; SB122 — uiux-settings-groups §5, шаг 5):
 * «Шаблон» (4 схемы в цветах сайта), «Что на карточке» (цена — всегда;
 * короткого описания у «Компактной» нет), «Кнопка и фото», «Шрифт»
 * (подсказка о шрифте карточки, `CATALOG_CARD_FONT_PARTS`). «Вид карточки»
 * и «Подписи бейджей» — свои файлы. Каждая группа — `SettingsGroup` со
 * сводкой для свёрнутого вида.
 */

import { CATALOG_CARD_FONT_PARTS, FONT_ROLES, PRODUCT_CARD_TEMPLATES, type SiteCatalogOptions, type SiteColors } from "@chaiberi/shared";

import { FieldHint, GroupField, SettingsGroup } from "../../ui/settings-groups";
import { ElementFontNote } from "../../site-builder/section/elements/ElementFontNote";
import { CheckRow } from "../CheckRow";
import { Segmented } from "../Segmented";
import { CardTemplatePicker } from "./CardTemplatePicker";
import { useElementFonts } from "../../../hooks/site-builder/useElementFonts";
import type { CardTemplateMemory } from "../../../lib/site-view/catalog-form";

type Card = SiteCatalogOptions["card"];
type Fields = Card["fields"];

/** Подсказка о старой цене (P3, uiux-design-r2a §2). */
export const OLD_PRICE_HINT = "Старая цена показывается, только когда цена правда снижена: прежняя цена действовала не меньше 30 дней.";

/** Поля карточки: подпись строки и короткое слово для сводки. */
const FIELDS: ReadonlyArray<{ key: keyof Fields; label: string; short: string }> = [
  { key: "badge", label: "Бейдж", short: "бейдж" },
  { key: "skuPicker", label: "Фасовка «от 50 г»", short: "фасовка" },
  { key: "rating", label: "Рейтинг", short: "рейтинг" },
  { key: "shortDescription", label: "Короткое описание (2 строки)", short: "описание" },
];

const BUTTON = [
  { value: "text" as const, label: "Текстом", short: "кнопка текстом" },
  { value: "icon" as const, label: "Значком", short: "кнопка значком" },
  { value: "none" as const, label: "Без кнопки", short: "без кнопки" },
];

const ASPECT = [
  { value: "1:1" as const, label: "1:1" },
  { value: "4:5" as const, label: "4:5" },
  { value: "3:4" as const, label: "3:4" },
];

interface CardProps {
  card: Card;
  setCard: (patch: Partial<Card>) => void;
}

interface TemplateProps {
  card: Card;
  colors: SiteColors | null;
  memory: CardTemplateMemory;
  onTemplate: (template: Card["template"]) => void;
}

export function CardTemplateGroup({ card, colors, memory, onTemplate }: TemplateProps) {
  const label = PRODUCT_CARD_TEMPLATES.find((t) => t.key === card.template)?.label;
  return (
    <SettingsGroup id="template" title="Шаблон" hint="Как устроена карточка: где фото и где текст." summary={label}>
      <CardTemplatePicker value={card.template} colors={colors} onChange={onTemplate} />
      {memory.desktop ? <FieldHint className="mt-0">«В ряд на ПК» — 2 для этой карточки; при другом шаблоне вернётся {memory.desktop}.</FieldHint> : null}
    </SettingsGroup>
  );
}

export function CardFieldsGroup({ card, setCard }: CardProps) {
  const compact = card.template === "compact";
  const shown = (key: keyof Fields) => card.fields[key] && !(key === "shortDescription" && compact);
  const summary = ["цена", ...FIELDS.filter((f) => shown(f.key)).map((f) => f.short)].join(", ");
  return (
    <SettingsGroup id="fields" title="Что на карточке" tocLabel="На карточке" hint="Что видно под фото, кроме названия." summary={summary}>
      <div className="flex min-w-0 flex-col">
        {FIELDS.map((f) => {
          const off = f.key === "shortDescription" && compact;
          return (
            <div key={f.key} className="flex flex-col">
              <CheckRow checked={shown(f.key)} disabled={off} onChange={(on) => setCard({ fields: { ...card.fields, [f.key]: on } })}>
                {f.label}
              </CheckRow>
              {off ? <FieldHint className="mt-0 pl-7">У компактной карточки короткого описания нет.</FieldHint> : null}
            </div>
          );
        })}
        <FieldHint>Цена видна всегда. {OLD_PRICE_HINT}</FieldHint>
      </div>
    </SettingsGroup>
  );
}

export function CardButtonPhotoGroup({ card, setCard }: CardProps) {
  const button = BUTTON.find((b) => b.value === card.buyButton)?.short ?? "";
  return (
    <SettingsGroup id="button-photo" title="Кнопка и фото" tocLabel="Кнопка, фото" hint="Кнопка «в корзину» и форма фото." summary={`${button} · фото ${card.imageAspect}`}>
      <GroupField label="Кнопка «в корзину»">
        <Segmented aria-label="Кнопка в корзину" options={BUTTON} value={card.buyButton} onChange={(buyButton) => setCard({ buyButton })} />
        <FieldHint className="mt-0">У товара с несколькими фасовками кнопка — «Выбрать фасовку».</FieldHint>
      </GroupField>
      <GroupField label="Пропорции фото">
        <Segmented aria-label="Пропорции фото" options={ASPECT} value={card.imageAspect} onChange={(imageAspect) => setCard({ imageAspect })} />
      </GroupField>
    </SettingsGroup>
  );
}

export function CardFontGroup() {
  const model = useElementFonts(CATALOG_CARD_FONT_PARTS);
  // Сводка: «Основной текст · Inter» — роль и шрифт, что реально стоят на сайте.
  const summary = model?.lines.map((l) => `${FONT_ROLES[l.appliedRole].title}${l.family ? ` · ${l.family}` : ""}`).join(", ");
  return (
    <SettingsGroup id="font" title="Шрифт" hint="Шрифт общий для всего сайта, здесь — только какой стоит." summary={summary}>
      {model ? (
        <ElementFontNote parts={CATALOG_CARD_FONT_PARTS} backLabel="Карточка товара (каталог)" back={{ panel: "catalog", sub: "card" }} />
      ) : (
        // Пустая группа не скрывается: оформление сайта ещё не пришло.
        <FieldHint className="mt-0">Оформление сайта ещё не загрузилось — шрифт карточки покажем, как только оно придёт.</FieldHint>
      )}
    </SettingsGroup>
  );
}
